import type { FC } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import type { DetailRow } from '../useExpandedViewContent';
import { getStackBadge } from '../stackBadges';
import { LuCode } from 'react-icons/lu';

interface CaseDetailsSectionProps {
  detailRows: DetailRow[];
  hasDetails: boolean;
  detailsExpanded: boolean;
  onToggle: () => void;
}

const CaseDetailsSection: FC<CaseDetailsSectionProps> = ({ detailRows, hasDetails, detailsExpanded, onToggle }) => (
  <section className="font-montreal text-zinc-800 dark:text-white/80">
    <div className="mx-auto w-full max-w-[96rem] px-6 sm:px-10 lg:px-16">
      <header className="flex flex-col gap-4 pb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-3xl font-light tracking-tight text-zinc-900 dark:text-white md:text-[2.5rem]">
            Case details
          </h3>
        </div>
        {hasDetails && (
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex items-center gap-2 self-start rounded-full border border-zinc-300 bg-white px-4 py-2 text-xs font-medium uppercase tracking-[0.28em] text-zinc-600 transition hover:border-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 dark:border-white/15 dark:bg-white/5 dark:text-white/70 dark:hover:border-white/25 dark:hover:bg-white/10 dark:hover:text-white"
          >
            {detailsExpanded ? 'Collapse card' : 'Expand card'}
            <svg
              className={`size-4 transition-transform ${detailsExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </header>

      <AnimatePresence initial={false}>
        {detailsExpanded && hasDetails && (
          <motion.div
            key="case-details"
            initial={{ opacity: 0, y: -12, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -12, height: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="rounded-3xl border border-zinc-200 bg-zinc-50 px-6 py-8 shadow-none sm:px-8 md:px-10 md:py-12 dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[0_18px_45px_rgba(0,0,0,0.25)]">
              <div className="flex flex-col">
                {detailRows.map((row) => (
                  <div
                    key={row.key}
                    className="flex flex-col gap-6 py-8 first:pt-0 last:pb-0 md:flex-row md:items-start md:gap-16"
                  >
                    <div className="w-full md:flex-[0_0_37.5%] md:pr-8">
                      {row.label ? (
                        <span className="block text-xl font-light text-zinc-800 md:text-[1.5rem] dark:text-white/85">
                          {row.label}
                        </span>
                      ) : null}
                    </div>
                    <div className="w-full min-w-0 md:flex-[0_0_62.5%] md:max-w-full md:pl-8 md:pr-12">
                      {row.type === 'list' && (
                        <ul
                          className={`min-w-0 max-w-full space-y-6 break-words whitespace-normal pl-5 ${
                            row.emphasize
                              ? 'text-[2.5rem] leading-tight text-zinc-900 md:text-[3.75rem] dark:text-white'
                              : 'text-lg leading-relaxed text-zinc-700 md:text-xl dark:text-white/80'
                          }`}
                        >
                          {row.items.map((item, index) => (
                            <li
                              key={index}
                              className="list-disc break-words whitespace-normal marker:text-zinc-400 dark:marker:text-white/45"
                            >
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}

                      {row.type === 'paragraphs' && (
                        <div
                          className={`min-w-0 max-w-full space-y-4 break-words whitespace-normal text-base leading-relaxed text-zinc-700 md:text-lg dark:text-white/80 ${
                            row.key === 'task'
                              ? 'text-xl font-medium text-zinc-900 md:text-2xl dark:text-white'
                              : ''
                          }`}
                        >
                          {row.items.map((text, index) => (
                            <p key={index} className="break-words whitespace-normal">
                              {text}
                            </p>
                          ))}
                        </div>
                      )}

                      {row.type === 'testimonial' && (
                        <figure className="min-w-0 max-w-full overflow-hidden rounded-[24px] border border-transparent bg-white px-6 py-7 text-left text-zinc-700 shadow-none sm:px-8 md:mr-6 dark:border-white/12 dark:bg-white/[0.06] dark:text-white/90 dark:shadow-[0_18px_45px_rgba(0,0,0,0.25)]">
                          <blockquote className="break-words whitespace-normal text-base leading-relaxed md:text-lg">
                            {row.quote}
                          </blockquote>
                          {(row.author || row.role) && (
                            <figcaption className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-zinc-500 dark:text-white/70">
                              <div className="flex items-center gap-3">
                                <div className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-zinc-200 bg-zinc-100 text-base font-semibold uppercase text-zinc-700 dark:border-white/15 dark:bg-white/[0.08] dark:text-white/90">
                                  {row.avatarUrl ? (
                                    <img
                                      src={row.avatarUrl}
                                      alt={row.author ?? 'Testimonial author'}
                                      className="size-full object-cover"
                                    />
                                  ) : (
                                    (row.author ?? 'client')
                                      .split(/\s+/)
                                      .map((part) => part.charAt(0).toUpperCase())
                                      .join('')
                                      .slice(0, 2)
                                      .padEnd(2, '•')
                                  )}
                                </div>
                                <div className="flex min-w-0 flex-col">
                                  {row.author ? (
                                    <span className="break-words whitespace-normal text-zinc-700 dark:text-white/90">
                                      {row.author}
                                    </span>
                                  ) : null}
                                  {row.role ? (
                                    <span className="break-words whitespace-normal text-zinc-500 dark:text-white/60">
                                      {row.role}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                              <svg
                                className="size-5 shrink-0 text-zinc-400 dark:text-white/45"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                viewBox="0 0 24 24"
                              >
                                <path d="M5 12h14" strokeLinecap="round" />
                                <path d="m13 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </figcaption>
                          )}
                        </figure>
                      )}

                      {row.type === 'chips' && (
                        <div className="flex min-w-0 max-w-full flex-wrap gap-3">
                          {row.items.map((stackItem) => {
                            const badge = getStackBadge(stackItem);
                            const Icon = badge?.icon ?? LuCode;
                            return (
                              <div
                                key={stackItem}
                                className="flex max-w-full items-center gap-2 rounded-full border border-zinc-200 bg-zinc-100 px-5 py-2 text-sm text-zinc-700 dark:border-white/15 dark:bg-white/[0.03] dark:text-white/80"
                              >
                                <Icon className="size-4 text-zinc-500 dark:text-white/70" aria-hidden />
                                <span className="max-w-full break-words whitespace-normal">
                                  {badge?.label ?? stackItem}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </section>
);

export default CaseDetailsSection;
